import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, CalendarCheck, ShoppingBag, Ticket, MessageSquare, ShieldCheck,
  LayoutDashboard, DollarSign, PiggyBank, Trash2, Plus, Check, Loader2, LogOut,
  Link2, ExternalLink, Star, UserSquare2, Pencil, X, Upload, Tag, Gift, UserPlus, Mail,
  Images, FileText, TrendingUp, Newspaper, Sparkles, Eye, EyeOff, Share2, Download, QrCode, Package,
  Headphones, CalendarClock, RefreshCw, History, GraduationCap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import Academy from "./Academy";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["members", "Members", Users],
  ["referrals", "Referrals", Share2],
  ["checkins", "Check-ins", CalendarCheck],
  ["orders", "Orders", ShoppingBag],
  ["redemptions", "Redemptions", Ticket],
  ["rewards", "Rewards", Gift],
  ["inquiries", "Inquiries", MessageSquare],
  ["customer-service", "Customer Service", Headphones],
  ["schedule-report", "Daily Schedule", CalendarClock],
  ["academy", "AestheticAcademy", GraduationCap],
  ["team", "Team", UserSquare2],
  ["promos", "Promos", Tag],
  ["gallery", "Gallery", Images],
  ["content", "Service Content", FileText],
  ["campaigns", "Campaigns", TrendingUp],
  ["blog", "Blog", Newspaper],
  ["enroll", "New Signup", UserPlus],
  ["links", "Staff Links", Link2],
  ["staff", "Staff", ShieldCheck],
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // allowed_tabs === null/undefined means full access. The owner is always full-access.
  const allowed = user?.is_owner ? null : user?.allowed_tabs;
  const visibleTabs = allowed == null
    ? TABS
    : TABS.filter(([key]) => key === "overview" || allowed.includes(key));
  const visibleKeys = visibleTabs.map(([k]) => k);
  const requested = params.get("tab") || "overview";
  const tab = visibleKeys.includes(requested) ? requested : "overview";

  return (
    <div className="min-h-screen bg-stone" data-testid="admin-portal">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-iri-mid">Staff Portal</p>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-ink mt-1">Welcome, {user?.name?.split(" ")[0]}</h1>
          </div>
          <Button variant="ghost" onClick={() => logout().then(() => navigate("/"))} data-testid="admin-logout" className="rounded-full text-ink gap-2">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-8 border-b border-slate-200">
          {visibleTabs.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setParams({ tab: key })}
              data-testid={`admin-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm whitespace-nowrap transition-colors ${
                tab === key ? "text-ink font-medium border-b-2 border-iri-mid -mb-[2px]" : "text-[#8A9492] hover:text-ink"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview />}
        {tab === "members" && <Members />}
        {tab === "referrals" && <Referrals />}
        {tab === "checkins" && <Checkins />}
        {tab === "orders" && <Orders />}
        {tab === "redemptions" && <Redemptions />}
        {tab === "rewards" && <RewardsManage />}
        {tab === "inquiries" && <Inquiries />}
        {tab === "customer-service" && <CustomerService />}
        {tab === "schedule-report" && <ScheduleReport />}
        {tab === "academy" && <Academy isOwner={user?.is_owner} />}
        {tab === "team" && <TeamManage />}
        {tab === "promos" && <PromosManage />}
        {tab === "gallery" && <GalleryManage />}
        {tab === "content" && <ContentManage />}
        {tab === "campaigns" && <CampaignsView />}
        {tab === "blog" && <BlogManage />}
        {tab === "enroll" && <EnrollClient />}
        {tab === "links" && <StaffLinks />}
        {tab === "staff" && <Staff currentId={user?.id} />}
      </div>
    </div>
  );
}

function Card({ children, testid }) {
  return <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={testid}>{children}</div>;
}

function Empty({ text }) {
  return <p className="text-sm text-[#8A9492] p-8 text-center">{text}</p>;
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Empty text="Loading…" />;
  const stats = [
    ["Members", s.members, Users, false],
    ["Checked in today", s.checkins_today, CalendarCheck, true],
    ["Shop orders", s.orders, ShoppingBag, false],
    ["Shop revenue", money(s.revenue), DollarSign, false],
    ["Member savings", money(s.member_savings), PiggyBank, false],
    ["Rewards to honor", s.redemptions_pending, Ticket, true],
    ["Google reviews", s.google_reviews ?? 0, Star, false],
    ["Yelp reviews", s.yelp_reviews ?? 0, Star, false],
  ];
  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-stats">
        {stats.map(([label, val, Icon, hot]) => (
          <div key={label} className={`rounded-2xl p-7 ${hot ? "iri-gradient" : "bg-white border border-slate-100"}`}>
            <Icon className={`h-6 w-6 ${hot ? "text-ink/70" : "text-iri-mid"}`} strokeWidth={1.5} />
            <p className={`text-sm mt-4 ${hot ? "text-ink/70" : "text-[#8A9492]"}`}>{label}</p>
            <p className="font-heading text-4xl font-light text-ink mt-1">{val}</p>
          </div>
        ))}
      </div>
      <MarketingAssets />
    </div>
  );
}

const MARKETING_ASSETS = [
  { key: "banner", label: "Rewards QR banner", meta: "PNG · 854×480 · landscape",
    desc: "For screens, emails & waiting-room monitors.",
    src: "/rewards-signup-qr-banner.png", file: "iridescent-rewards-signup-banner.png", preview: true },
  { key: "tent", label: "Rewards QR table tent", meta: "PNG · 1080×1350 · portrait",
    desc: "Print for the front desk & treatment rooms.",
    src: "/rewards-signup-qr-tent.png", file: "iridescent-rewards-signup-tent.png", preview: true },
  { key: "flyer", label: "Client rewards flyer", meta: "PDF · 1 page",
    desc: "Points, rewards & referral overview to hand out.",
    src: "/iridescent-rewards-benefits.pdf", file: "iridescent-rewards-benefits.pdf", preview: false, Icon: FileText },
  { key: "pack", label: "Front-desk print pack", meta: "ZIP · all 3 assets",
    desc: "Banner, table tent & flyer bundled to print in one go.",
    src: "/iridescent-rewards-print-pack.zip", file: "iridescent-rewards-print-pack.zip", preview: false, Icon: Package },
  { key: "bundle", label: "Rewards marketing bundle", meta: "ZIP · staff guide, flyer, email + QR",
    desc: "Everything for the rewards program: staff playbook, client flyer, email template, hero image & QR assets.",
    src: "/iridescent-rewards-marketing-bundle.zip", file: "iridescent-rewards-marketing-bundle.zip", preview: false, Icon: Package },
  { key: "staff", label: "Staff rewards playbook", meta: "PDF · training guide",
    desc: "How to talk about the website, rewards & memberships with confidence.",
    src: "/iridescent-staff-rewards-playbook.pdf", file: "iridescent-staff-rewards-playbook.pdf", preview: false, Icon: FileText },
  { key: "email-tpl", label: "Rewards email template", meta: "HTML · ready to send",
    desc: "Details, sign-up, benefits & referral emphasis. Copy into any email platform.",
    src: "/iridescent-rewards-email.html", file: "iridescent-rewards-email.html", preview: false, Icon: Mail },
  { key: "email-hero", label: "Rewards email hero image", meta: "PNG · 1200×600",
    desc: "Branded banner graphic used at the top of the rewards email.",
    src: "/rewards-email-hero.png", file: "iridescent-rewards-email-hero.png", preview: true },
  { key: "mem-video", label: "Membership lobby video", meta: "MP4 · 1080×1904 · 21:37 loop",
    desc: "Animated lobby-screen loop — VIP membership invite.",
    thumb: "/lobby-membership-poster.png", src: "/lobby-membership.mp4", file: "iridescent-membership-lobby.mp4", preview: true },
  { key: "rew-video", label: "Rewards lobby video", meta: "MP4 · 1080×1904 · 21:37 loop",
    desc: "Animated lobby-screen loop — free rewards + QR to join.",
    thumb: "/lobby-rewards-poster.png", src: "/lobby-rewards.mp4", file: "iridescent-rewards-lobby.mp4", preview: true },
  { key: "mem-poster", label: "Membership lobby poster", meta: "PNG · 1080×1904 · 21:37",
    desc: "Static version of the membership screen.",
    src: "/lobby-membership-poster.png", file: "iridescent-membership-lobby-poster.png", preview: true },
  { key: "rew-poster", label: "Rewards lobby poster", meta: "PNG · 1080×1904 · 21:37",
    desc: "Static version of the rewards screen.",
    src: "/lobby-rewards-poster.png", file: "iridescent-rewards-lobby-poster.png", preview: true },
  { key: "svc-video", label: "New services lobby video", meta: "MP4 · 1080×1904 · 21:37 loop",
    desc: "Animated lobby loop — SmoothGlo, LipGlo & Smooth Tone + QR to explore.",
    thumb: "/services-lobby-poster.png", src: "/services-lobby.mp4", file: "iridescent-services-lobby.mp4", preview: true },
  { key: "folix-video", label: "FOLIX lobby video", meta: "MP4 · 1080×1904 · 21:37 loop",
    desc: "Animated lobby loop — FOLIX laser hair restoration + QR to explore.",
    thumb: "/folix-lobby-poster.png", src: "/folix-lobby.mp4", file: "iridescent-folix-lobby.mp4", preview: true },
  { key: "svc-poster", label: "New services lobby poster", meta: "PNG · 1080×1904 · 21:37",
    desc: "Static version of the new services screen.",
    src: "/services-lobby-poster.png", file: "iridescent-services-lobby-poster.png", preview: true },
  { key: "folix-poster", label: "FOLIX lobby poster", meta: "PNG · 1080×1904 · 21:37",
    desc: "Static version of the FOLIX screen.",
    src: "/folix-lobby-poster.png", file: "iridescent-folix-lobby-poster.png", preview: true },
  { key: "brands-video", label: "Nutrafol + DermaGenix lobby video", meta: "MP4 · 1080×1904 · 21:37 loop",
    desc: "Animated lobby loop — hair & skin product lines + QR to shop.",
    thumb: "/brands-lobby-poster.png", src: "/brands-lobby.mp4", file: "iridescent-brands-lobby.mp4", preview: true },
  { key: "brands-poster", label: "Nutrafol + DermaGenix poster", meta: "PNG · 1080×1904 · 21:37",
    desc: "Static version of the hair & skin screen.",
    src: "/brands-lobby-poster.png", file: "iridescent-brands-lobby-poster.png", preview: true },
];

function SendRewardsEmail() {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (audience) => {
    if (!audience && !to.trim()) { toast.error("Enter at least one email address"); return; }
    if (audience === "members" && !window.confirm("Send the rewards email to ALL members with an email on file?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rewards/send-email", audience ? { audience } : { to });
      toast.success(`Sent to ${data.sent} of ${data.total}${data.failed ? ` · ${data.failed} failed` : ""}`);
      if (!audience) setTo("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send email");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5 mt-5" data-testid="send-rewards-email">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
        <h3 className="font-heading text-lg font-medium text-ink">Send the rewards email</h3>
      </div>
      <p className="text-sm text-[#8A9492] mb-4">Sends the branded rewards email (benefits, sign-up & referral) using your Iridescent email. Enter recipients or send to all members.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="email@example.com, another@example.com"
          data-testid="rewards-email-to" className="rounded-lg flex-1" />
        <Button onClick={() => send(null)} disabled={busy} data-testid="rewards-email-send"
          className="iri-gradient text-ink rounded-full h-11 px-6 font-medium">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Send</>}
        </Button>
        <Button onClick={() => send("members")} disabled={busy} variant="outline" data-testid="rewards-email-send-all"
          className="rounded-full h-11 px-6 font-medium border-slate-200">
          Send to all members
        </Button>
      </div>
    </div>
  );
}

function MarketingAssets() {
  return (
    <div data-testid="marketing-assets">
      <div className="flex items-center gap-2 mb-4">
        <QrCode className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
        <h2 className="font-heading text-2xl font-light text-ink">Marketing assets</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {MARKETING_ASSETS.map((a) => (
          <div key={a.key} className="rounded-2xl bg-white border border-slate-100 p-5 flex flex-col" data-testid={`asset-${a.key}`}>
            <div className="rounded-xl overflow-hidden bg-stone mb-4 flex items-center justify-center" style={{ aspectRatio: "16 / 9" }}>
              {a.preview
                ? <img src={a.thumb || a.src} alt={a.label} className="w-full h-full object-contain" />
                : <a.Icon className="h-10 w-10 text-iri-mid" strokeWidth={1.25} />}
            </div>
            <p className="text-ink font-medium">{a.label}</p>
            <p className="text-xs uppercase tracking-wider text-[#8A9492] mt-0.5">{a.meta}</p>
            <p className="text-sm text-[#8A9492] mt-2 flex-1">{a.desc}</p>
            <a href={a.src} download={a.file} data-testid={`asset-download-${a.key}`}
              className="mt-4 inline-flex items-center justify-center gap-2 h-11 rounded-full iri-gradient text-ink text-sm font-medium">
              <Download className="h-4 w-4" /> Download
            </a>
          </div>
        ))}
      </div>
      <SendRewardsEmail />
    </div>
  );
}

function BlogManage() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "" });
  const load = () => api.get("/admin/blog").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy("gen");
    try {
      const { data } = await api.post("/admin/blog/generate");
      toast.success(`Draft created: ${data.title}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation failed"); }
    finally { setBusy(""); }
  };

  const act = async (slug, verb, fn) => {
    setBusy(slug + verb);
    try { await fn(); toast.success("Done"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const publish = (p) => act(p.slug, "pub", () => api.put(`/admin/blog/${p.slug}/publish`));
  const unpublish = (p) => act(p.slug, "unpub", () => api.put(`/admin/blog/${p.slug}/unpublish`));
  const remove = (p) => { if (window.confirm(`Delete "${p.title}"?`)) act(p.slug, "del", () => api.delete(`/admin/blog/${p.slug}`)); };

  const startEdit = (p) => { setEditing(p.slug); setForm({ title: p.title, excerpt: p.excerpt || "", content: p.content || "" }); };
  const saveEdit = async (slug) => {
    setBusy(slug + "save");
    try { await api.put(`/admin/blog/${slug}`, form); toast.success("Saved"); setEditing(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const isDraft = (p) => (p.status || "published") === "draft";

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#8A9492] max-w-xl">
          AI drafts a fresh, educational Macomb-County article every 2 weeks. Review, edit, then publish — nothing goes live until you approve it.
        </p>
        <Button onClick={generate} disabled={busy === "gen"} data-testid="blog-generate-btn" className="rounded-full iri-gradient text-ink gap-2">
          {busy === "gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate draft now
        </Button>
      </div>

      <Card testid="blog-list">
        {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No posts yet. Generate your first draft →" /> : (
          <div className="divide-y divide-slate-100">
            {rows.map((p) => (
              <div key={p.slug} className="p-4" data-testid={`blog-row-${p.slug}`}>
                <div className="flex items-center gap-4">
                  <img src={p.image} alt="" className="h-14 w-20 rounded-lg object-cover bg-stone shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-ink font-medium truncate">{p.title}</p>
                      {isDraft(p)
                        ? <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-testid={`blog-status-${p.slug}`}>Draft</span>
                        : <span className="text-[10px] uppercase tracking-wider text-green-700 bg-green-50 px-2 py-0.5 rounded-full" data-testid={`blog-status-${p.slug}`}>Published</span>}
                      {p.ai_generated && <span className="text-[10px] uppercase tracking-wider text-iri-mid bg-stone px-2 py-0.5 rounded-full">AI</span>}
                    </div>
                    <p className="text-xs text-[#8A9492] mt-0.5 truncate">{p.excerpt}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {isDraft(p) ? (
                      <Button size="sm" onClick={() => publish(p)} disabled={busy === p.slug + "pub"} data-testid={`blog-publish-${p.slug}`} className="rounded-full bg-ink text-white h-8 text-xs gap-1">
                        {busy === p.slug + "pub" ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Eye className="h-3 w-3" /> Publish</>}
                      </Button>
                    ) : (
                      <button onClick={() => unpublish(p)} data-testid={`blog-unpublish-${p.slug}`} className="text-[#8A9492] hover:text-ink transition-colors" title="Move to draft"><EyeOff className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => (editing === p.slug ? setEditing(null) : startEdit(p))} data-testid={`blog-edit-${p.slug}`} className="text-[#8A9492] hover:text-ink transition-colors"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(p)} data-testid={`blog-delete-${p.slug}`} className="text-[#8A9492] hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                {editing === p.slug && (
                  <div className="mt-4 space-y-3 rounded-xl bg-stone p-4" data-testid={`blog-editor-${p.slug}`}>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={`blog-title-input-${p.slug}`} placeholder="Title" />
                    <Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid={`blog-excerpt-input-${p.slug}`} placeholder="Excerpt" rows={2} />
                    <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid={`blog-content-input-${p.slug}`} placeholder="Content" rows={10} />
                    <Button onClick={() => saveEdit(p.slug)} disabled={busy === p.slug + "save"} data-testid={`blog-save-${p.slug}`} className="rounded-full iri-gradient text-ink h-9 text-sm">
                      {busy === p.slug + "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Referrals() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/referrals").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const confirm = async (id) => {
    setBusy(id);
    try {
      const res = await api.post(`/admin/referrals/${id}/confirm`);
      toast.success(`Confirmed — ${res.data.points_awarded} points released to the referrer`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not confirm");
    } finally { setBusy(null); }
  };

  const badge = (r) => {
    if (r.status === "confirmed") return <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700">Confirmed · +{r.points_awarded || 500}</span>;
    if (r.status === "signed_up") return <span className={`text-xs px-2.5 py-1 rounded-full ${r.purchased ? "bg-amber-100 text-amber-700" : "bg-stone text-ink"}`}>{r.purchased ? "Purchased · ready" : "Signed up"}</span>;
    return <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-[#8A9492]">Invited</span>;
  };

  return (
    <Card testid="admin-referrals">
      {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No referrals yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone text-[#8A9492] text-xs uppercase tracking-wider">
              <tr><th className="text-left p-4">Referred by</th><th className="text-left p-4">Friend</th><th className="text-left p-4">Status</th><th className="text-left p-4">Invited</th><th className="text-right p-4">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`referral-row-${r.id}`}>
                  <td className="p-4 text-ink font-medium">{r.referrer_name || "—"}<div className="text-xs text-[#8A9492]">{r.referrer_code}</div></td>
                  <td className="p-4 text-[#5C6664]">{r.friend_name}{r.friend_phone && <div className="text-xs text-[#8A9492]">{r.friend_phone}</div>}</td>
                  <td className="p-4">{badge(r)}</td>
                  <td className="p-4 text-[#8A9492]">{fmtDate(r.created_at)}</td>
                  <td className="p-4 text-right">
                    {r.status === "signed_up" ? (
                      <Button size="sm" disabled={busy === r.id} onClick={() => confirm(r.id)} data-testid={`confirm-referral-${r.id}`}
                        className="rounded-full h-8 text-xs iri-gradient text-ink">
                        {busy === r.id ? "…" : "Confirm friend purchased"}
                      </Button>
                    ) : r.status === "confirmed" ? (
                      <span className="text-xs text-green-600 inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Rewarded</span>
                    ) : (
                      <span className="text-xs text-[#b7b2c2]">Awaiting sign-up</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Members() {
  const [rows, setRows] = useState(null);
  const [adjust, setAdjust] = useState(null); // member being adjusted
  const load = () => api.get("/admin/members").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <Card testid="admin-members">
      {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No members yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone text-[#8A9492] text-xs uppercase tracking-wider">
              <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Tier</th><th className="text-right p-4">Points</th><th className="text-right p-4">Visits</th><th className="text-left p-4">Joined</th><th className="text-right p-4">Points</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((m) => (
                <tr key={m.id} data-testid={`member-row-${m.id}`}>
                  <td className="p-4 text-ink font-medium">{m.name}</td>
                  <td className="p-4 text-[#5C6664]">{m.email}</td>
                  <td className="p-4"><span className="text-xs bg-stone px-2.5 py-1 rounded-full text-ink">{m.tier}</span></td>
                  <td className="p-4 text-right text-ink">{m.points}</td>
                  <td className="p-4 text-right text-[#5C6664]">{m.total_visits}</td>
                  <td className="p-4 text-[#8A9492]">{fmtDate(m.created_at)}</td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="outline" className="rounded-full h-8 text-xs"
                      data-testid={`adjust-points-${m.id}`} onClick={() => setAdjust(m)}>
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> Adjust
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adjust && <AdjustPointsDialog member={adjust} onClose={() => setAdjust(null)} onSaved={load} />}
    </Card>
  );
}

function AdjustPointsDialog({ member, onClose, onSaved }) {
  const [mode, setMode] = useState("add"); // add | remove
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);
  const [points, setPoints] = useState(member.points);

  useEffect(() => {
    api.get(`/admin/members/${member.id}/points-log`).then((r) => setLog(r.data)).catch(() => setLog([]));
  }, [member.id]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) { toast.error("Enter a whole number greater than 0"); return; }
    if (!reason.trim()) { toast.error("Please add a reason"); return; }
    setBusy(true);
    try {
      const delta = mode === "add" ? amt : -amt;
      const res = await api.post(`/admin/members/${member.id}/points`, { delta, reason: reason.trim() });
      setPoints(res.data.points);
      toast.success(`${mode === "add" ? "Added" : "Removed"} ${Math.abs(res.data.applied)} points · new balance ${res.data.points}`);
      setAmount(""); setReason("");
      const r = await api.get(`/admin/members/${member.id}/points-log`);
      setLog(r.data);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update points");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="adjust-points-dialog" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-heading text-lg text-ink">Adjust reward points</h3>
            <p className="text-xs text-[#8A9492]">{member.name} · {member.email}</p>
          </div>
          <button onClick={onClose} className="text-[#8A9492] hover:text-ink" data-testid="adjust-close"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="rounded-xl bg-stone p-3 text-center">
            <span className="text-xs text-[#8A9492] uppercase tracking-wider">Current balance</span>
            <p className="text-2xl font-heading text-ink" data-testid="adjust-current-balance">{points} pts</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMode("add")} data-testid="adjust-mode-add"
              className={`h-10 rounded-full text-sm font-medium transition-colors ${mode === "add" ? "iri-gradient text-ink" : "bg-stone text-[#5C6664]"}`}>+ Add points</button>
            <button type="button" onClick={() => setMode("remove")} data-testid="adjust-mode-remove"
              className={`h-10 rounded-full text-sm font-medium transition-colors ${mode === "remove" ? "bg-ink text-white" : "bg-stone text-[#5C6664]"}`}>− Remove points</button>
          </div>
          <div>
            <label className="text-sm text-ink">Amount</label>
            <Input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 50" className="mt-1 rounded-lg h-11" data-testid="adjust-amount" />
          </div>
          <div>
            <label className="text-sm text-ink">Reason (required)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="e.g. Goodwill credit for a delayed appointment" className="mt-1 rounded-lg" data-testid="adjust-reason" />
          </div>
          <Button type="submit" disabled={busy} data-testid="adjust-submit"
            className="w-full iri-gradient text-ink rounded-full h-11 font-medium">
            {busy ? "Saving…" : mode === "add" ? "Add points" : "Remove points"}
          </Button>

          {log && log.length > 0 && (
            <div className="pt-2" data-testid="adjust-history">
              <p className="text-xs text-[#8A9492] uppercase tracking-wider mb-2">Recent adjustments</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {log.slice(0, 8).map((e, i) => (
                  <div key={i} className="text-xs flex items-start justify-between gap-3 border-b border-slate-50 pb-1.5">
                    <div>
                      <span className={e.delta_applied >= 0 ? "text-iri-mid font-medium" : "text-ink font-medium"}>
                        {e.delta_applied >= 0 ? "+" : ""}{e.delta_applied} pts
                      </span>
                      <span className="text-[#8A9492]"> · {e.reason || "—"}</span>
                      {e.staff_name && <span className="text-[#b7b2c2]"> · {e.staff_name}</span>}
                    </div>
                    <span className="text-[#b7b2c2] whitespace-nowrap">{fmtDate(e.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function Checkins() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/checkins").then((r) => setData(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-5">
      {data && (
        <div className="iri-gradient rounded-2xl p-6 inline-block" data-testid="admin-checkins-today">
          <p className="text-ink/70 text-sm">Checked in today</p>
          <p className="font-heading text-4xl font-light text-ink">{data.checked_in_today}</p>
        </div>
      )}
      <Card testid="admin-checkins">
        {!data ? <Empty text="Loading…" /> : data.checkins.length === 0 ? <Empty text="No check-ins recorded yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone text-[#8A9492] text-xs uppercase tracking-wider">
                <tr><th className="text-left p-4">Member</th><th className="text-left p-4">Email</th><th className="text-right p-4">Points</th><th className="text-left p-4">Date</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.checkins.map((c, i) => (
                  <tr key={i}>
                    <td className="p-4 text-ink font-medium">{c.name}</td>
                    <td className="p-4 text-[#5C6664]">{c.email}</td>
                    <td className="p-4 text-right text-iri-mid">+{c.points}</td>
                    <td className="p-4 text-[#8A9492]">{c.at ? new Date(c.at).toLocaleString() : c.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Orders() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/orders").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Card testid="admin-orders">
      {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No orders yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone text-[#8A9492] text-xs uppercase tracking-wider">
              <tr><th className="text-left p-4">Customer</th><th className="text-left p-4">Items</th><th className="text-left p-4">Fulfillment</th><th className="text-right p-4">Total</th><th className="text-right p-4">Saved</th><th className="text-left p-4">Date</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <td className="p-4 text-ink font-medium">{o.customer_name || o.email}</td>
                  <td className="p-4 text-[#5C6664]">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                  <td className="p-4" data-testid={`order-fulfillment-${o.id}`}>
                    {o.fulfillment === "ship" ? (
                      <span className="text-xs">
                        <span className="text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Ship</span>
                        {o.shipping_address && (
                          <span className="block text-[#8A9492] mt-1 leading-tight">
                            {o.shipping_address.name}, {o.shipping_address.line1}
                            {o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}, {o.shipping_address.city} {o.shipping_address.state} {o.shipping_address.zip}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-green-700 bg-green-50 px-2.5 py-1 rounded-full">Pick-up</span>
                    )}
                  </td>
                  <td className="p-4 text-right text-ink">{money(o.amount)}</td>
                  <td className="p-4 text-right text-iri-mid">{o.savings ? money(o.savings) : "—"}</td>
                  <td className="p-4 text-[#8A9492]">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Redemptions() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/redemptions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markUsed = async (r) => {
    setBusy(r.id);
    try {
      await api.post("/admin/redemptions/use", { user_id: r.user_id, redemption_id: r.id });
      toast.success("Reward marked as used");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  return (
    <Card testid="admin-redemptions">
      {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No redemptions yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone text-[#8A9492] text-xs uppercase tracking-wider">
              <tr><th className="text-left p-4">Member</th><th className="text-left p-4">Reward</th><th className="text-left p-4">Code</th><th className="text-left p-4">Status</th><th className="text-right p-4">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`redemption-row-${r.id}`}>
                  <td className="p-4 text-ink font-medium">{r.user_name}</td>
                  <td className="p-4 text-[#5C6664]">{r.reward_name}</td>
                  <td className="p-4 font-mono text-ink">{r.code}</td>
                  <td className="p-4">
                    {r.used
                      ? <span className="text-xs text-green-700 bg-green-50 px-2.5 py-1 rounded-full">Used</span>
                      : <span className="text-xs text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Pending</span>}
                  </td>
                  <td className="p-4 text-right">
                    {!r.used && (
                      <Button size="sm" onClick={() => markUsed(r)} disabled={busy === r.id} data-testid={`mark-used-${r.id}`} className="rounded-full bg-ink text-white h-8 text-xs">
                        {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Mark used</>}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Inquiries() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/submissions").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Card testid="admin-inquiries">
      {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No inquiries yet." /> : (
        <div className="divide-y divide-slate-100">
          {rows.map((q, i) => (
            <div key={i} className="p-5" data-testid={`inquiry-row-${i}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-ink font-medium">{q.name} <span className="text-[#8A9492] font-normal">· {q.email}</span></p>
                <span className="text-xs bg-stone px-2.5 py-1 rounded-full text-ink">{q.tier ? `Membership: ${q.tier}` : (q.type || "Contact")}</span>
              </div>
              {q.phone && <p className="text-xs text-[#8A9492] mt-1">{q.phone}</p>}
              {q.service_interest && <p className="text-xs text-iri-mid mt-1">Interested in: {q.service_interest}</p>}
              {q.message && <p className="text-sm text-[#5C6664] mt-2">{q.message}</p>}
              <p className="text-xs text-[#8A9492] mt-2">{fmtDate(q.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const CS_STATUSES = ["new", "assigned", "open", "reached out", "pending resolution", "escalated", "resolved"];
const CS_STATUS_STYLE = {
  "new": "bg-slate-100 text-slate-600",
  "assigned": "bg-blue-50 text-blue-600",
  "open": "bg-indigo-50 text-indigo-600",
  "reached out": "bg-cyan-50 text-cyan-700",
  "pending resolution": "bg-amber-50 text-amber-700",
  "escalated": "bg-red-50 text-red-600",
  "resolved": "bg-emerald-50 text-emerald-700",
};

function CustomerService() {
  const [rows, setRows] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState({ customer_name: "", service_date: "", notes: "", assignee_id: "" });
  const [busy, setBusy] = useState(false);
  const [resDraft, setResDraft] = useState({});     // ticketId -> resolution text (while resolving)
  const [resolving, setResolving] = useState({});   // ticketId -> bool
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fAssignee, setFAssignee] = useState("all");
  const [needsAttention, setNeedsAttention] = useState(false);
  const [openLog, setOpenLog] = useState({});

  const load = () => api.get("/admin/cs/tickets").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/cs/assignees").then((r) => setAssignees(r.data)).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/cs/tickets", form);
      toast.success("Ticket created");
      setForm({ customer_name: "", service_date: "", notes: "", assignee_id: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    try { await api.put(`/admin/cs/tickets/${id}`, body); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onStatus = (t, val) => {
    if (val === "resolved") { setResolving((s) => ({ ...s, [t.id]: true })); setResDraft((s) => ({ ...s, [t.id]: t.resolution || "" })); return; }
    patch(t.id, { status: val });
  };

  const saveResolution = async (t) => {
    const text = (resDraft[t.id] || "").trim();
    if (!text) { toast.error("Enter resolution details"); return; }
    await patch(t.id, { status: "resolved", resolution: text });
    setResolving((s) => ({ ...s, [t.id]: false }));
    toast.success("Ticket resolved");
  };

  const remove = async (t) => { if (window.confirm(`Delete ticket for ${t.customer_name}?`)) { await api.delete(`/admin/cs/tickets/${t.id}`); await load(); } };

  const runReminders = async () => {
    try { const { data } = await api.post("/admin/cs/run-reminders"); toast.success(`Reminders sent: ${data.sent} (escalated ${data.escalated})`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendTest = async () => {
    const to = window.prompt("Send a test email to which address?");
    if (!to) return;
    try { const { data } = await api.post("/admin/cs/test-email", { to }); toast.success(`Test email sent to ${data.to}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send test email"); }
  };

  const daysSince = (iso) => { const t = new Date(iso).getTime(); return Number.isNaN(t) ? 0 : Math.floor((Date.now() - t) / 86400000); };
  const isOverdue = (t) => t.status !== "resolved" && daysSince(t.status_changed_at || t.created_at) >= 5;
  const fmtDateTime = (s) => { try { return new Date(s).toLocaleString("en-US", { timeZone: "America/Detroit", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return s; } };
  const filtered = (rows || []).filter((t) => {
    if (fStatus !== "all" && t.status !== fStatus) return false;
    if (fAssignee === "unassigned" && t.assignee_id) return false;
    if (fAssignee !== "all" && fAssignee !== "unassigned" && t.assignee_id !== fAssignee) return false;
    if (needsAttention && !(t.status !== "resolved" && (t.status === "new" || t.status === "assigned" || isOverdue(t)))) return false;
    if (q.trim() && !`${t.customer_name} ${t.notes || ""}`.toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-customer-service">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#8A9492] max-w-xl">
          Track customer complaints & service follow-ups. The assignee gets a daily email while a ticket is <strong>New</strong> or <strong>Assigned</strong>; after 5 days with no change, their manager is CC'd.
        </p>
        <div className="flex items-center gap-2">
          <Button onClick={sendTest} data-testid="cs-test-email" variant="outline" className="rounded-full gap-2 h-10">
            <Mail className="h-4 w-4" /> Send test email
          </Button>
          <Button onClick={runReminders} data-testid="cs-run-reminders" className="rounded-full bg-ink text-white gap-2 h-10">
            <RefreshCw className="h-4 w-4" /> Send reminders now
          </Button>
        </div>
      </div>

      <Card testid="cs-new-form">
        <form onSubmit={create} className="p-5 grid sm:grid-cols-2 gap-3">
          <Input required placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="cs-name-input" />
          <div>
            <label className="text-xs text-[#8A9492]">Complaint / service date</label>
            <Input type="date" value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} data-testid="cs-date-input" className="mt-1" />
          </div>
          <Textarea placeholder="Notes / what happened" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cs-notes-input" rows={2} className="sm:col-span-2" />
          <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="cs-assignee-select" className="h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink">
            <option value="">Assign to… (optional)</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.email}</option>)}
          </select>
          <Button type="submit" disabled={busy} data-testid="cs-create-btn" className="iri-gradient text-ink rounded-full h-11 font-medium">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add ticket</>}
          </Button>
        </form>
      </Card>

      <Card testid="cs-filters">
        <div className="p-4 flex items-center gap-2 flex-wrap">
          <Input placeholder="Search name or notes…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cs-search" className="w-56" />
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="cs-filter-status" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink capitalize">
            <option value="all">All statuses</option>
            {CS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fAssignee} onChange={(e) => setFAssignee(e.target.value)} data-testid="cs-filter-assignee" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink">
            <option value="all">All assignees</option>
            <option value="unassigned">Unassigned</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={() => setNeedsAttention((v) => !v)} data-testid="cs-filter-attention" className={`h-10 px-4 rounded-full text-sm transition-colors ${needsAttention ? "bg-red-600 text-white" : "border border-slate-200 text-[#5C6664] hover:border-slate-300"}`}>Needs attention</button>
          <span className="text-xs text-[#8A9492] ml-auto" data-testid="cs-filter-count">{filtered.length} of {(rows || []).length}</span>
        </div>
      </Card>

      <Card testid="cs-list">
        {!rows ? <Empty text="Loading…" /> : filtered.length === 0 ? <Empty text={(rows.length === 0) ? "No customer-service tickets yet." : "No tickets match your filters."} /> : (
          <div className="divide-y divide-slate-100">
            {filtered.map((t) => (
              <div key={t.id} className="p-5" data-testid={`cs-row-${t.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <p className="text-ink font-medium">{t.customer_name} <span className="text-[#8A9492] font-normal text-sm">· {t.service_date || "no date"}</span></p>
                    {t.notes && <p className="text-sm text-[#5C6664] mt-1 max-w-2xl">{t.notes}</p>}
                    {t.resolution && <p className="text-sm text-emerald-700 mt-1"><strong>Resolution:</strong> {t.resolution}</p>}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {isOverdue(t) && <span className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-red-100 text-red-700" data-testid={`cs-overdue-${t.id}`}>5+ days no update</span>}
                    <span className={`text-xs font-medium px-3 py-1.5 rounded-full ${CS_STATUS_STYLE[t.status] || "bg-slate-100"}`} data-testid={`cs-status-badge-${t.id}`}>{t.status}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <select value={CS_STATUSES.includes(t.status) ? t.status : "new"} onChange={(e) => onStatus(t, e.target.value)} data-testid={`cs-status-select-${t.id}`} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink capitalize">
                    {CS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={t.assignee_id || ""} onChange={(e) => patch(t.id, { assignee_id: e.target.value })} data-testid={`cs-assignee-${t.id}`} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink">
                    <option value="">Unassigned</option>
                    {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <button onClick={() => remove(t)} data-testid={`cs-delete-${t.id}`} className="ml-auto text-[#8A9492] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
                {(resolving[t.id] || (t.status === "resolved")) && (
                  <div className="mt-3 rounded-xl bg-stone p-3" data-testid={`cs-resolution-box-${t.id}`}>
                    <label className="text-xs text-[#8A9492]">Resolution details</label>
                    <Textarea value={resDraft[t.id] !== undefined ? resDraft[t.id] : (t.resolution || "")} onChange={(e) => setResDraft((s) => ({ ...s, [t.id]: e.target.value }))} rows={2} data-testid={`cs-resolution-input-${t.id}`} className="mt-1 bg-white" />
                    <Button onClick={() => saveResolution(t)} data-testid={`cs-resolution-save-${t.id}`} className="mt-2 iri-gradient text-ink rounded-full h-9 text-sm">Save resolution</Button>
                  </div>
                )}
                <button onClick={() => setOpenLog((s) => ({ ...s, [t.id]: !s[t.id] }))} data-testid={`cs-activity-toggle-${t.id}`} className="mt-3 text-xs text-iri-mid inline-flex items-center gap-1 hover:text-ink">
                  <History className="h-3.5 w-3.5" /> Activity ({(t.activity || []).length})
                </button>
                {openLog[t.id] && (
                  <ul className="mt-2 border-l-2 border-slate-100 pl-4 space-y-2" data-testid={`cs-activity-${t.id}`}>
                    {(t.activity || []).length === 0 && <li className="text-xs text-[#8A9492]">No activity recorded yet.</li>}
                    {(t.activity || []).slice().reverse().map((ev, i) => (
                      <li key={i} className="text-xs text-[#5C6664]" data-testid={`cs-activity-item-${t.id}`}>
                        <span className="text-ink font-medium capitalize">{ev.type}</span> — {ev.detail}
                        <span className="block text-[#8A9492]">{fmtDateTime(ev.ts)} · {ev.actor}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function ScheduleReport() {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Detroit" });
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (d) => {
    setBusy(true); setData(null);
    try { const r = await api.get(`/admin/schedule-report?date=${d || date}`); setData(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not load report"); } finally { setBusy(false); }
  };
  useEffect(() => { run(today); /* eslint-disable-next-line */ }, []);

  const fmtTime = (s) => { try { return new Date(s).toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "numeric", minute: "2-digit" }); } catch { return s; } };

  const downloadCsv = () => {
    const rows = data?.rows || [];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Time", "Client", "Services", "Provider", "Phone", "Email", "Tags"].join(",")].concat(
      rows.map((r) => [fmtTime(r.start_at), r.client_name, (r.services || []).join("; "), r.provider || "", r.phone || "", r.email || "", (r.tags || []).join("; ")].map(esc).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `iridescent-schedule-${data.date}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const printReport = () => {
    const rows = data?.rows || [];
    const w = window.open("", "_blank"); if (!w) return;
    const body = rows.map((r) => `<tr><td>${fmtTime(r.start_at)}</td><td>${r.client_name}</td><td>${(r.services || []).join(", ")}</td><td>${r.provider || ""}</td><td>${(r.tags || []).join(", ")}</td><td>${[r.phone, r.email].filter(Boolean).join("<br>")}</td></tr>`).join("");
    w.document.write(`<html><head><title>Daily Schedule ${data.date}</title><style>body{font-family:Arial,sans-serif;padding:28px;color:#1f1a32}h1{font-size:20px;margin:0}p{color:#8A9492;font-size:13px}table{width:100%;border-collapse:collapse;margin-top:14px}th,td{border-bottom:1px solid #e5e0ea;padding:8px 10px;text-align:left;font-size:13px;vertical-align:top}th{background:#f4f2f0;text-transform:uppercase;font-size:11px;letter-spacing:.5px}</style></head><body><h1>Daily Schedule — ${data.date}</h1><p>LAST VISIT / MID F/U · ${rows.length} appointment(s)</p><table><thead><tr><th>Time</th><th>Client</th><th>Services</th><th>Provider</th><th>Tags</th><th>Contact</th></tr></thead><tbody>${body}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  };

  return (
    <div className="space-y-6" data-testid="admin-schedule-report">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-[#8A9492] max-w-xl">Clients scheduled in Boulevard on the selected date whose appointment is tagged <strong>LAST VISIT</strong>, <strong>MID F/U</strong>, or <strong>1 VISIT</strong>.</p>
          <div className="flex items-center gap-2 mt-3">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="schedule-date-input" className="w-44" />
            <Button onClick={() => run()} disabled={busy} data-testid="schedule-run-btn" className="iri-gradient text-ink rounded-full h-11 gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Run report
            </Button>
          </div>
        </div>
      </div>

      {data && !data.configured && (
        <Card><Empty text="Boulevard isn't configured in this environment, so no appointments can be pulled." /></Card>
      )}
      <Card testid="schedule-list">
        {!data ? <Empty text="Loading…" /> : data.count === 0 ? <Empty text={`No LAST VISIT / MID F/U appointments on ${data.date}.`} /> : (
          <div className="divide-y divide-slate-100">
            <div className="p-3 px-5 text-xs text-[#8A9492]">{data.count} appointment{data.count > 1 ? "s" : ""} on {data.date}</div>
            {data.rows.map((r) => (
              <div key={r.id} className="p-5 flex items-start justify-between flex-wrap gap-3" data-testid={`schedule-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-ink font-medium">{fmtTime(r.start_at)} · {r.client_name}</p>
                  <p className="text-sm text-[#5C6664]">{(r.services || []).join(", ") || "Appointment"}{r.provider ? ` · ${r.provider}` : ""}</p>
                  <p className="text-xs text-[#8A9492] mt-0.5">{[r.phone, r.email].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {(r.tags || []).map((t) => (
                    <span key={t} className={`text-xs font-medium px-2.5 py-1 rounded-full ${/last/i.test(t) ? "bg-purple-50 text-purple-700" : /1\s*visit/i.test(t) ? "bg-amber-50 text-amber-700" : "bg-teal-50 text-teal-700"}`} data-testid={`schedule-tag-${r.id}`}>{t}</span>
                  ))}
                  {r.manage_url && <a href={r.manage_url} target="_blank" rel="noreferrer" className="text-iri-mid hover:text-ink" data-testid={`schedule-manage-${r.id}`}><ExternalLink className="h-4 w-4" /></a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const MANAGEABLE_TABS = TABS.filter(([k]) => k !== "overview");

function Staff({ currentId }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [tabSel, setTabSel] = useState(() => MANAGEABLE_TABS.map(([k]) => k)); // all by default
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTabs, setEditTabs] = useState([]);
  const load = () => api.get("/admin/staff").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    const allTabs = MANAGEABLE_TABS.map(([k]) => k);
    const allowed_tabs = tabSel.length === allTabs.length ? null : tabSel; // null = full access
    try {
      await api.post("/admin/staff", { ...form, allowed_tabs });
      toast.success("Staff account created");
      setForm({ name: "", email: "", password: "" });
      setTabSel(allTabs);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setEditTabs(s.allowed_tabs == null ? MANAGEABLE_TABS.map(([k]) => k) : s.allowed_tabs);
  };

  const savePerms = async (s) => {
    const allTabs = MANAGEABLE_TABS.map(([k]) => k);
    const allowed_tabs = editTabs.length === allTabs.length ? null : editTabs;
    try {
      await api.put(`/admin/staff/${s.id}/permissions`, { allowed_tabs });
      toast.success("Access updated");
      setEditId(null);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}'s staff access?`)) return;
    try {
      await api.delete(`/admin/staff/${s.id}`);
      toast.success("Staff account removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const accessLabel = (s) => (s.is_owner || s.allowed_tabs == null ? "Full access" : `${s.allowed_tabs.length} section${s.allowed_tabs.length === 1 ? "" : "s"}`);

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <Card testid="admin-staff-list">
        <div className="p-5 border-b border-slate-100"><h2 className="font-heading text-lg font-medium text-ink">Staff accounts</h2></div>
        {!rows ? <Empty text="Loading…" /> : (
          <div className="divide-y divide-slate-100">
            {rows.map((s) => (
              <div key={s.id} className="p-4" data-testid={`staff-row-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-ink font-medium">{s.name} {s.is_owner && <span className="text-xs iri-gradient text-ink px-2 py-0.5 rounded-full ml-1">Owner</span>}</p>
                    <p className="text-xs text-[#8A9492]">{s.email}</p>
                    <p className="text-xs text-iri-mid mt-1" data-testid={`staff-access-${s.id}`}>{accessLabel(s)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {!s.is_owner && (
                      <button onClick={() => (editId === s.id ? setEditId(null) : startEdit(s))} data-testid={`edit-access-${s.id}`} className="text-[#8A9492] hover:text-ink transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {!s.is_owner && s.id !== currentId && (
                      <button onClick={() => remove(s)} data-testid={`remove-staff-${s.id}`} className="text-[#8A9492] hover:text-red-500 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                {editId === s.id && (
                  <div className="mt-3 rounded-xl bg-stone p-3" data-testid={`staff-perms-editor-${s.id}`}>
                    <p className="text-xs text-[#5C6664] mb-2">Sections this staff can see (Overview is always visible):</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {MANAGEABLE_TABS.map(([k, label]) => (
                        <label key={k} className="flex items-center gap-2 text-xs text-ink">
                          <input type="checkbox" checked={editTabs.includes(k)} onChange={() => toggle(editTabs, setEditTabs, k)} data-testid={`perm-edit-${s.id}-${k}`} /> {label}
                        </label>
                      ))}
                    </div>
                    <Button onClick={() => savePerms(s)} data-testid={`save-access-${s.id}`} className="mt-3 h-8 text-xs rounded-full bg-ink text-white">Save access</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card testid="admin-staff-form">
        <form onSubmit={add} className="p-5 space-y-4">
          <h2 className="font-heading text-lg font-medium text-ink flex items-center gap-2"><Plus className="h-4 w-4" /> Add staff member</h2>
          <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" />
          <Input required type="password" placeholder="Temporary password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" />
          <div className="rounded-xl border border-slate-200 p-3" data-testid="staff-tabs-select">
            <p className="text-xs font-medium text-ink mb-2">Portal sections this staff can access</p>
            <div className="grid grid-cols-2 gap-1.5">
              {MANAGEABLE_TABS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs text-[#5C6664]">
                  <input type="checkbox" checked={tabSel.includes(k)} onChange={() => toggle(tabSel, setTabSel, k)} data-testid={`staff-tab-${k}`} /> {label}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-[#8A9492] mt-2">Overview is always visible. Leave all checked for full access.</p>
          </div>
          <Button type="submit" disabled={busy} data-testid="create-staff-btn" className="w-full iri-gradient text-ink rounded-full h-11 font-medium">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create staff account"}
          </Button>
          <p className="text-xs text-[#8A9492]">New staff sign in at the member login and see only the sections you allow. Share credentials securely.</p>
        </form>
      </Card>
    </div>
  );
}

function StaffLinks() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ label: "", url: "", category: "Payment Plans" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/links").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/links", form);
      toast.success("Link added");
      setForm({ label: "", url: "", category: form.category });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (l) => {
    try {
      await api.delete(`/admin/links/${l.id}`);
      toast.success("Link removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const grouped = (rows || []).reduce((acc, l) => {
    (acc[l.category] = acc[l.category] || []).push(l);
    return acc;
  }, {});

  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="admin-links">
      <div className="md:col-span-2 space-y-6">
        {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? (
          <Card><Empty text="No staff links yet. Add your first one →" /></Card>
        ) : (
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} data-testid={`link-group-${cat}`}>
              <h3 className="text-xs uppercase tracking-[0.2em] text-iri-mid mb-3">{cat}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {items.map((l) => (
                  <div key={l.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between gap-3 hover-lift" data-testid={`link-row-${l.id}`}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-ink font-medium text-sm min-w-0" data-testid={`link-open-${l.id}`}>
                      <span className="h-9 w-9 rounded-lg iri-gradient flex items-center justify-center shrink-0">
                        <ExternalLink className="h-4 w-4 text-ink" />
                      </span>
                      <span className="truncate">{l.label}</span>
                    </a>
                    <button onClick={() => remove(l)} data-testid={`remove-link-${l.id}`} className="text-[#8A9492] hover:text-red-500 transition-colors shrink-0">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <Card testid="admin-links-form">
        <form onSubmit={add} className="p-5 space-y-4">
          <h2 className="font-heading text-lg font-medium text-ink flex items-center gap-2"><Plus className="h-4 w-4" /> Add a staff link</h2>
          <Input required placeholder="Label (e.g. ADP — HR Login)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="link-label-input" />
          <Input required placeholder="URL (e.g. adp.com)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="link-url-input" />
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="link-category-select"
            className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink"
          >
            {["Payment Plans", "Email", "HR", "Scheduling", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <Button type="submit" disabled={busy} data-testid="create-link-btn" className="w-full iri-gradient text-ink rounded-full h-11 font-medium">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add link"}
          </Button>
          <p className="text-xs text-[#8A9492]">Quick access to the tools your team uses — payment-plan portals, email, HR, scheduling and more. Opens in a new tab.</p>
        </form>
      </Card>
    </div>
  );
}


const EMPTY_MEMBER = { name: "", title: "", bio: "", photo_url: "", fun_facts: "", top_products: "", favorite_treatment: "", order: 99, hidden: false };

function TeamManage() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // member id or "new"
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/team").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: r.data.url }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const startNew = () => { setForm({ ...EMPTY_MEMBER, order: (rows?.length || 0) }); setEditing("new"); };
  const startEdit = (m) => { setForm({ ...EMPTY_MEMBER, ...m }); setEditing(m.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY_MEMBER); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing === "new") await api.post("/admin/team", payload);
      else await api.put(`/admin/team/${editing}`, payload);
      toast.success("Team member saved");
      cancel();
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team page?`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success("Team member removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="admin-team">
      <div className="flex justify-end">
        <Button onClick={startNew} data-testid="team-add-btn" className="rounded-full iri-gradient text-ink gap-2">
          <Plus className="h-4 w-4" /> Add team member
        </Button>
      </div>

      {editing && (
        <Card testid="team-form-card">
          <form onSubmit={save} className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-medium text-ink">{editing === "new" ? "New team member" : "Edit team member"}</h2>
              <button type="button" onClick={cancel} data-testid="team-cancel-btn" className="text-[#8A9492] hover:text-ink"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="team-name-input" />
              <Input placeholder="Title / role" value={form.title} onChange={set("title")} data-testid="team-title-input" />
            </div>
            <Input placeholder="Photo URL" value={form.photo_url} onChange={set("photo_url")} data-testid="team-photo-input" className="hidden" />
            <div className="flex items-center gap-4">
              <label className="text-sm text-[#5C6664] w-24 shrink-0">Photo</label>
              {form.photo_url ? (
                <img src={form.photo_url} alt="preview" className="h-16 w-16 rounded-full object-cover bg-stone shrink-0" data-testid="team-photo-preview" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-stone flex items-center justify-center text-[#8A9492] shrink-0"><UserSquare2 className="h-6 w-6" /></div>
              )}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="team-photo-upload" />
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-ink hover:bg-stone transition-colors">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {form.photo_url ? "Change photo" : "Upload photo"}
                </span>
              </label>
            </div>
            <Textarea placeholder="Bio" value={form.bio} onChange={set("bio")} data-testid="team-bio-input" rows={3} />
            <Textarea placeholder="Fun Facts (leave blank to hide)" value={form.fun_facts} onChange={set("fun_facts")} data-testid="team-funfacts-input" rows={2} />
            <Textarea placeholder="Top 2 Dermagenix Products (leave blank to hide)" value={form.top_products} onChange={set("top_products")} data-testid="team-products-input" rows={2} />
            <Textarea placeholder="Can't Live Without Treatment (leave blank to hide)" value={form.favorite_treatment} onChange={set("favorite_treatment")} data-testid="team-treatment-input" rows={2} />
            <div className="flex items-center gap-3">
              <label className="text-sm text-[#5C6664]">Display order</label>
              <Input type="number" className="w-24" value={form.order} onChange={set("order")} data-testid="team-order-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#5C6664] rounded-xl bg-stone p-3" data-testid="team-hidden-wrap">
              <input type="checkbox" checked={form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} data-testid="team-hidden-checkbox" />
              Hidden — signature only (not shown on the public Team page, but selectable as an email-signature consultant)
            </label>
            <Button type="submit" disabled={busy} data-testid="team-save-btn" className="iri-gradient text-ink rounded-full h-11 px-8 font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save team member"}
            </Button>
          </form>
        </Card>
      )}

      <Card testid="team-list">
        {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No team members yet. Add your first one →" /> : (
          <div className="divide-y divide-slate-100">
            {rows.map((m) => (
              <div key={m.id} className="p-4 flex items-center gap-4" data-testid={`team-row-${m.id}`}>
                <img src={m.photo_url} alt={m.name} className="h-14 w-14 rounded-full object-cover bg-stone shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-ink font-medium truncate">{m.name} <span className="text-[#8A9492] font-normal text-xs">· #{m.order}</span>
                    {m.hidden && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#8A9492] bg-stone px-2 py-0.5 rounded-full" data-testid={`team-hidden-badge-${m.id}`}>Hidden</span>}
                  </p>
                  <p className="text-xs text-iri-mid">{m.title}</p>
                </div>
                <button onClick={() => startEdit(m)} data-testid={`team-edit-${m.id}`} className="text-[#8A9492] hover:text-ink transition-colors"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(m)} data-testid={`team-delete-${m.id}`} className="text-[#8A9492] hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const EMPTY_PROMO = { code: "", discount_type: "percent", value: 10, description: "", active: true };

function PromosManage() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // promo id or "new"
  const [form, setForm] = useState(EMPTY_PROMO);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/promos").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY_PROMO); setEditing("new"); };
  const startEdit = (p) => { setForm({ ...EMPTY_PROMO, ...p }); setEditing(p.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY_PROMO); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, code: form.code.trim().toUpperCase(), value: Number(form.value) || 0 };
    try {
      if (editing === "new") await api.post("/admin/promos", payload);
      else await api.put(`/admin/promos/${editing}`, payload);
      toast.success("Promo code saved");
      cancel();
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/promos/${p.id}`, { ...p, active: !p.active });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo code ${p.code}?`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success("Promo code removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const fmtVal = (p) => (p.discount_type === "percent" ? `${p.value}% off` : `$${p.value} off`);

  return (
    <div className="space-y-6" data-testid="admin-promos">
      <div className="flex justify-end">
        <Button onClick={startNew} data-testid="promo-add-btn" className="rounded-full iri-gradient text-ink gap-2">
          <Plus className="h-4 w-4" /> Add promo code
        </Button>
      </div>

      {editing && (
        <Card testid="promo-form-card">
          <form onSubmit={save} className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-medium text-ink">{editing === "new" ? "New promo code" : "Edit promo code"}</h2>
              <button type="button" onClick={cancel} data-testid="promo-cancel-btn" className="text-[#8A9492] hover:text-ink"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Input required placeholder="CODE (e.g. WELCOME20)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" className="uppercase" />
              <select value={form.discount_type} onChange={set("discount_type")} data-testid="promo-type-select" className="h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink">
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed ($)</option>
              </select>
              <Input required type="number" step="0.01" min="0" placeholder="Value" value={form.value} onChange={set("value")} data-testid="promo-value-input" />
            </div>
            <Input placeholder="Description (optional)" value={form.description} onChange={set("description")} data-testid="promo-desc-input" />
            <label className="flex items-center gap-2 text-sm text-[#5C6664]">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="promo-active-checkbox" /> Active
            </label>
            <Button type="submit" disabled={busy} data-testid="promo-save-btn" className="iri-gradient text-ink rounded-full h-11 px-8 font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save promo code"}
            </Button>
          </form>
        </Card>
      )}

      <Card testid="promo-list">
        {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No promo codes yet. Add your first one →" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone text-[#8A9492] text-xs uppercase tracking-wider">
                <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Discount</th><th className="text-left p-4">Description</th><th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => (
                  <tr key={p.id} data-testid={`promo-row-${p.id}`}>
                    <td className="p-4 font-mono font-medium text-ink">{p.code}</td>
                    <td className="p-4 text-iri-mid">{fmtVal(p)}</td>
                    <td className="p-4 text-[#5C6664]">{p.description || "—"}</td>
                    <td className="p-4">
                      <button onClick={() => toggleActive(p)} data-testid={`promo-toggle-${p.id}`}
                        className={`text-xs px-2.5 py-1 rounded-full ${p.active ? "text-green-700 bg-green-50" : "text-[#8A9492] bg-stone"}`}>
                        {p.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button onClick={() => startEdit(p)} data-testid={`promo-edit-${p.id}`} className="text-[#8A9492] hover:text-ink transition-colors"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(p)} data-testid={`promo-delete-${p.id}`} className="text-[#8A9492] hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}


const EMPTY_REWARD = { name: "", cost: 100, description: "" };

function RewardsManage() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // reward id or "new"
  const [form, setForm] = useState(EMPTY_REWARD);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/rewards").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY_REWARD); setEditing("new"); };
  const startEdit = (r) => { setForm({ ...EMPTY_REWARD, ...r }); setEditing(r.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY_REWARD); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name: form.name.trim(), cost: Number(form.cost) || 0, description: form.description.trim() };
    try {
      if (editing === "new") await api.post("/admin/rewards", payload);
      else await api.put(`/admin/rewards/${editing}`, payload);
      toast.success("Reward saved");
      cancel();
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete reward "${r.name}"?`)) return;
    try {
      await api.delete(`/admin/rewards/${r.id}`);
      toast.success("Reward removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="admin-rewards">
      <div className="flex justify-end">
        <Button onClick={startNew} data-testid="reward-add-btn" className="rounded-full iri-gradient text-ink gap-2">
          <Plus className="h-4 w-4" /> Add reward
        </Button>
      </div>

      {editing && (
        <Card testid="reward-form-card">
          <form onSubmit={save} className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-medium text-ink">{editing === "new" ? "New reward" : "Edit reward"}</h2>
              <button type="button" onClick={cancel} data-testid="reward-cancel-btn" className="text-[#8A9492] hover:text-ink"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Input required placeholder="Reward name" value={form.name} onChange={set("name")} data-testid="reward-name-input" className="sm:col-span-2" />
              <Input required type="number" min="0" placeholder="Points cost" value={form.cost} onChange={set("cost")} data-testid="reward-cost-input" />
            </div>
            <Textarea placeholder="Description" value={form.description} onChange={set("description")} data-testid="reward-desc-input" rows={2} />
            <Button type="submit" disabled={busy} data-testid="reward-save-btn" className="iri-gradient text-ink rounded-full h-11 px-8 font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save reward"}
            </Button>
          </form>
        </Card>
      )}

      <Card testid="reward-list">
        {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No rewards yet. Add your first one →" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone text-[#8A9492] text-xs uppercase tracking-wider">
                <tr><th className="text-left p-4">Reward</th><th className="text-left p-4">Description</th><th className="text-right p-4">Points</th><th className="text-right p-4">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`reward-row-${r.id}`}>
                    <td className="p-4 text-ink font-medium">{r.name}</td>
                    <td className="p-4 text-[#5C6664]">{r.description || "—"}</td>
                    <td className="p-4 text-right text-iri-mid font-medium">{r.cost}</td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button onClick={() => startEdit(r)} data-testid={`reward-edit-${r.id}`} className="text-[#8A9492] hover:text-ink transition-colors"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(r)} data-testid={`reward-delete-${r.id}`} className="text-[#8A9492] hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}


const EMPTY_ENROLL = { name: "", email: "", phone: "", password: "", tier: "", consultant: "", paid_in_office: false, free_cleanser: false };

function EnrollClient() {
  const [tiers, setTiers] = useState([]);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState(EMPTY_ENROLL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/membership-tiers").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const selectedTier = tiers.find((t) => t.name === form.tier);
  const isPaid = selectedTier && !selectedTier.free;

  const genPassword = () => setForm((f) => ({ ...f, password: "Iris" + Math.floor(1000 + Math.random() * 9000) + "!" }));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (isPaid && !form.paid_in_office) { toast.error("Confirm payment was collected in office for paid tiers"); return; }
    setBusy(true);
    const consultant = team.find((m) => m.id === form.consultant);
    try {
      const res = await api.post("/admin/enroll", {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || null,
        password: form.password, tier: form.tier, paid_in_office: form.paid_in_office,
        consultant_name: consultant?.name || null, consultant_title: consultant?.title || null,
        free_cleanser: form.free_cleanser,
      });
      setDone({ name: form.name, email: form.email, tier: form.tier, password: form.password, emailSent: res.data.email_sent, cleanserCode: res.data.cleanser_code });
      toast.success(`${form.name} enrolled in ${form.tier}`);
      setForm(EMPTY_ENROLL);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-enroll">
      {done && (
        <Card testid="enroll-success">
          <div className="p-6">
            <div className="flex items-center gap-2 text-green-700"><Check className="h-5 w-5" /><span className="font-medium">Enrollment complete</span></div>
            <p className="text-sm text-[#5C6664] mt-3"><strong>{done.name}</strong> is now a <strong>{done.tier}</strong>.</p>
            <div className="mt-3 rounded-xl bg-stone p-4 text-sm space-y-1">
              <p className="text-[#5C6664]">Share these login details with the client:</p>
              <p className="text-ink">Email: <span className="font-mono">{done.email}</span></p>
              <p className="text-ink">Temp password: <span className="font-mono">{done.password}</span></p>
            </div>
            <p className="text-sm mt-3 flex items-center gap-2 text-iri-mid" data-testid="enroll-email-status">
              <Mail className="h-4 w-4" /> {done.emailSent ? "Welcome email sent." : "Account created, but the welcome email could not be sent."}
            </p>
            {done.cleanserCode && (
              <div className="mt-3 rounded-xl border border-dashed border-iri-mid/40 bg-iri-mid/5 p-4 text-sm" data-testid="enroll-cleanser-code">
                <p className="text-[#5C6664]">Complimentary Revitalizing Cleanser code (single-use, 14-day expiry):</p>
                <p className="text-ink font-mono text-base mt-1">{done.cleanserCode}</p>
              </div>
            )}
            <Button onClick={() => setDone(null)} data-testid="enroll-another" className="mt-4 rounded-full iri-gradient text-ink">Enroll another client</Button>
          </div>
        </Card>
      )}

      {!done && (
        <Card testid="enroll-form-card">
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <h2 className="font-heading text-lg font-medium text-ink flex items-center gap-2"><UserPlus className="h-4 w-4" /> In-office membership signup</h2>
              <p className="text-xs text-[#8A9492] mt-1">Create the client's account and enroll them on the spot. A personalized welcome email is sent automatically.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input required placeholder="Client full name" value={form.name} onChange={set("name")} data-testid="enroll-name" />
              <Input required type="email" placeholder="Client email" value={form.email} onChange={set("email")} data-testid="enroll-email" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="enroll-phone" />
              <div className="flex gap-2">
                <Input required placeholder="Temp password (min 6)" value={form.password} onChange={set("password")} data-testid="enroll-password" />
                <Button type="button" onClick={genPassword} data-testid="enroll-genpass" className="rounded-lg bg-ink text-white shrink-0 px-3 text-xs">Generate</Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#8A9492]">Membership tier</label>
                <select required value={form.tier} onChange={set("tier")} data-testid="enroll-tier" className="mt-1 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink">
                  <option value="">Select a tier…</option>
                  {tiers.map((t) => (
                    <option key={t.slug} value={t.name}>{t.name}{t.free ? " (Free)" : ` ($${t.price}/mo)`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#8A9492]">Consultant (email signature)</label>
                <select value={form.consultant} onChange={set("consultant")} data-testid="enroll-consultant" className="mt-1 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-ink">
                  <option value="">Iridescent VIP Team</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} — {m.title}{m.hidden ? " (hidden)" : ""}</option>
                  ))}
                </select>
              </div>
            </div>
            {isPaid && (
              <label className="flex items-center gap-2 text-sm text-ink rounded-xl bg-stone p-3" data-testid="enroll-paid-wrap">
                <input type="checkbox" checked={form.paid_in_office} onChange={(e) => setForm({ ...form, paid_in_office: e.target.checked })} data-testid="enroll-paid-checkbox" />
                Client paid for this membership in office — activate the paid tier now.
              </label>
            )}
            <label className="flex items-start gap-2 text-sm text-ink rounded-xl bg-stone p-3" data-testid="enroll-cleanser-wrap">
              <input type="checkbox" className="mt-0.5" checked={form.free_cleanser} onChange={(e) => setForm({ ...form, free_cleanser: e.target.checked })} data-testid="enroll-cleanser-checkbox" />
              <span>Include a <strong>Complimentary Revitalizing Cleanser</strong> — generates a single-use code (14-day expiry) for a free cleanser in their Member Shop, added to the welcome email.</span>
            </label>
            <Button type="submit" disabled={busy} data-testid="enroll-submit" className="w-full iri-gradient text-ink rounded-full h-12 font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enroll client & send welcome email"}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}


const EMPTY_GALLERY = { treatment: "", title: "", before_url: "", after_url: "", order: 0 };

function GalleryManage() {
  const [rows, setRows] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_GALLERY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");

  const load = () => api.get("/admin/gallery").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const upload = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: r.data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const startNew = () => { setForm({ ...EMPTY_GALLERY, order: rows?.length || 0 }); setEditing("new"); };
  const startEdit = (g) => { setForm({ ...EMPTY_GALLERY, ...g }); setEditing(g.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY_GALLERY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.before_url || !form.after_url) { toast.error("Upload both a before and after image"); return; }
    setBusy(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing === "new") await api.post("/admin/gallery", payload);
      else await api.put(`/admin/gallery/${editing}`, payload);
      toast.success("Gallery item saved");
      cancel(); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (g) => {
    if (!window.confirm("Remove this before/after from the gallery?")) return;
    try { await api.delete(`/admin/gallery/${g.id}`); toast.success("Removed"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug || "—";

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      <div className="flex justify-end">
        <Button onClick={startNew} data-testid="gallery-add-btn" className="rounded-full iri-gradient text-ink gap-2">
          <Plus className="h-4 w-4" /> Add before/after
        </Button>
      </div>

      {editing && (
        <Card testid="gallery-form-card">
          <form onSubmit={save} className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-medium text-ink">{editing === "new" ? "New result" : "Edit result"}</h2>
              <button type="button" onClick={cancel} data-testid="gallery-cancel-btn" className="text-[#8A9492] hover:text-ink"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#5C6664]">Treatment category</label>
                <select value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} data-testid="gallery-treatment-select" className="mt-1 w-full rounded-lg border border-slate-200 h-11 px-3 text-sm">
                  <option value="">Uncategorized</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#5C6664]">Display order</label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="gallery-order-input" className="mt-1" />
              </div>
            </div>
            <Input placeholder="Caption (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-title-input" />
            <div className="grid sm:grid-cols-2 gap-4">
              {[["before_url", "Before"], ["after_url", "After"]].map(([field, label]) => (
                <div key={field} className="space-y-2">
                  <label className="text-sm text-[#5C6664]">{label} image</label>
                  {form[field] ? (
                    <img src={form[field]} alt={label} className="h-32 w-full rounded-lg object-cover bg-stone" data-testid={`gallery-${field}-preview`} />
                  ) : (
                    <div className="h-32 w-full rounded-lg bg-stone flex items-center justify-center text-[#8A9492]"><Images className="h-6 w-6" /></div>
                  )}
                  <label className="cursor-pointer inline-flex">
                    <input type="file" accept="image/*" className="hidden" onChange={upload(field)} data-testid={`gallery-${field}-upload`} />
                    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-ink hover:bg-stone transition-colors">
                      {uploading === field ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {form[field] ? "Change" : "Upload"}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <Button type="submit" disabled={busy} data-testid="gallery-save-btn" className="iri-gradient text-ink rounded-full h-11 px-8 font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save result"}
            </Button>
          </form>
        </Card>
      )}

      <Card testid="gallery-list">
        {!rows ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No before/after photos yet. Add your first one →" /> : (
          <div className="divide-y divide-slate-100">
            {rows.map((g) => (
              <div key={g.id} className="p-4 flex items-center gap-4" data-testid={`gallery-row-${g.id}`}>
                <div className="flex gap-1 shrink-0">
                  {g.before_url ? <img src={g.before_url} alt="before" className="h-14 w-14 rounded-lg object-cover bg-stone" /> : <div className="h-14 w-14 rounded-lg bg-stone" />}
                  {g.after_url ? <img src={g.after_url} alt="after" className="h-14 w-14 rounded-lg object-cover bg-stone" /> : <div className="h-14 w-14 rounded-lg bg-stone" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-ink font-medium truncate">{g.title || catName(g.treatment)} <span className="text-[#8A9492] font-normal text-xs">· #{g.order}</span></p>
                  <p className="text-xs text-iri-mid">{catName(g.treatment)}</p>
                </div>
                <button onClick={() => startEdit(g)} data-testid={`gallery-edit-${g.id}`} className="text-[#8A9492] hover:text-ink transition-colors"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(g)} data-testid={`gallery-delete-${g.id}`} className="text-[#8A9492] hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function ContentManage() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ what_to_expect: "", downtime: "", faqs: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/service-content").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEdit = (s) => {
    setForm({
      what_to_expect: (s.what_to_expect || []).join("\n"),
      downtime: s.downtime || "",
      faqs: (s.faqs || []).map((f) => `${f.q} || ${f.a}`).join("\n"),
    });
    setEditing(s.slug);
  };
  const cancel = () => { setEditing(null); };

  const save = async (slug) => {
    setBusy(true);
    const payload = {
      what_to_expect: form.what_to_expect.split("\n").map((s) => s.trim()).filter(Boolean),
      downtime: form.downtime.trim(),
      faqs: form.faqs.split("\n").map((line) => {
        const [q, ...a] = line.split("||");
        return { q: (q || "").trim(), a: a.join("||").trim() };
      }).filter((f) => f.q && f.a),
    };
    try {
      await api.put(`/admin/service-content/${slug}`, payload);
      toast.success("Service content saved");
      cancel(); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const reset = async (slug) => {
    if (!window.confirm("Reset this service to its default content?")) return;
    try { await api.delete(`/admin/service-content/${slug}`); toast.success("Reset to default"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!rows) return <Empty text="Loading…" />;

  const grouped = rows.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="admin-content">
      <p className="text-sm text-[#8A9492]">Edit the FAQs, "what to expect" steps and downtime shown on each service page. Blank fields fall back to sensible defaults.</p>
      {Object.entries(grouped).map(([cat, items]) => (
        <Card key={cat} testid={`content-cat-${cat}`}>
          <div className="px-5 py-3 bg-stone text-xs uppercase tracking-wider text-[#8A9492]">{(cat || "other").replace(/-/g, " ")}</div>
          <div className="divide-y divide-slate-100">
            {items.map((s) => (
              <div key={s.slug} className="p-4" data-testid={`content-row-${s.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-ink font-medium">{s.name} {s.customized && <span className="ml-1 text-[10px] uppercase tracking-wider text-iri-mid bg-stone px-2 py-0.5 rounded-full">Customized</span>}</p>
                    <p className="text-xs text-[#8A9492] truncate">Downtime: {s.downtime}</p>
                  </div>
                  {editing === s.slug ? (
                    <button onClick={cancel} data-testid={`content-cancel-${s.slug}`} className="text-[#8A9492] hover:text-ink"><X className="h-4 w-4" /></button>
                  ) : (
                    <>
                      {s.customized && <button onClick={() => reset(s.slug)} data-testid={`content-reset-${s.slug}`} className="text-xs text-[#8A9492] hover:text-red-500">Reset</button>}
                      <button onClick={() => startEdit(s)} data-testid={`content-edit-${s.slug}`} className="text-[#8A9492] hover:text-ink"><Pencil className="h-4 w-4" /></button>
                    </>
                  )}
                </div>

                {editing === s.slug && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="text-xs text-[#5C6664]">Downtime (one line)</label>
                      <Input value={form.downtime} onChange={(e) => setForm({ ...form, downtime: e.target.value })} data-testid={`content-downtime-${s.slug}`} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-[#5C6664]">What to expect (one step per line)</label>
                      <Textarea value={form.what_to_expect} onChange={(e) => setForm({ ...form, what_to_expect: e.target.value })} data-testid={`content-expect-${s.slug}`} rows={4} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-[#5C6664]">FAQs — one per line, format: Question || Answer</label>
                      <Textarea value={form.faqs} onChange={(e) => setForm({ ...form, faqs: e.target.value })} data-testid={`content-faqs-${s.slug}`} rows={4} className="mt-1" placeholder="How many sessions? || Most clients see best results after 3." />
                    </div>
                    <Button onClick={() => save(s.slug)} disabled={busy} data-testid={`content-save-${s.slug}`} className="iri-gradient text-ink rounded-full h-10 px-6 font-medium">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function CampaignsView() {
  const [c, setC] = useState(null);
  const [running, setRunning] = useState(false);
  useEffect(() => { api.get("/admin/campaigns").then((r) => setC(r.data)).catch(() => {}); }, []);

  const runUpgrade = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/upgrade-emails/run");
      toast.success(`Upgrade emails: ${data.sent} sent (${data.eligible} eligible)`);
      const r = await api.get("/admin/campaigns"); setC(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setRunning(false); }
  };

  if (!c) return <Empty text="Loading…" />;
  const cards = [
    ["Free members", c.free_members, false],
    ["Paid members", c.paid_members, true],
    ["Free to paid conversion", `${c.conversion_rate}%`, true],
    ["Upgrade emails sent", c.upgrade_emails_sent, false],
    ["Gift cards sold", c.gift_cards_sold, false],
    ["Gift card revenue", money(c.gift_cards_revenue), false],
    ["Referral signups", c.referral_signups, false],
    ["Referrals rewarded", c.referrals_completed, true],
    ["Referrals pending", c.referrals_pending, false],
  ];
  return (
    <div className="space-y-6" data-testid="admin-campaigns">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map(([label, val, hot]) => (
          <div key={label} className={`rounded-2xl p-7 ${hot ? "iri-gradient" : "bg-white border border-slate-100"}`} data-testid={`campaign-stat-${label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <p className={`text-sm ${hot ? "text-ink/70" : "text-[#8A9492]"}`}>{label}</p>
            <p className="font-heading text-4xl font-light text-ink mt-1">{val}</p>
          </div>
        ))}
      </div>
      <Card testid="campaign-actions">
        <div className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-ink font-medium">$99/mo Insider upgrade email</p>
            <p className="text-sm text-[#8A9492] mt-0.5">Sent automatically 24h after a free signup. Run it now for anyone already eligible.</p>
          </div>
          <Button onClick={runUpgrade} disabled={running} data-testid="campaign-run-upgrade" className="rounded-full iri-gradient text-ink gap-2 shrink-0">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send now
          </Button>
        </div>
      </Card>
    </div>
  );
}
